import { ConnectionManager } from "@solomon-labs/solana-connection";
import { RPC_ENDPOINT } from "@solomon-apps/config";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { Layout } from "./components/Layout";
import { NotFound } from "./pages/NotFound";
import type { AppConfig } from "./types/app";
import { WalletProvider } from "./wallet";

// Create a QueryClient instance for TanStack React Query
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            refetchOnWindowFocus: false,
            retry: 2
        }
    }
});

export async function createApp({
    rootSelector = "#root",
    routes,
    components: { layout: LayoutComponent = Layout } = {},
    pages: { notFound: NotFoundPage = NotFound } = {},
    setup
}: AppConfig): Promise<void> {
    // Initialize ConnectionManager before anything else
    ConnectionManager.setEndpoint(RPC_ENDPOINT);

    // Note: We intentionally skip CacheManager.init() - the caching package has
    // serialization bugs with BigInt/PublicKey that cause more problems than it solves.
    // The solomon-labs packages will work without caching, just with fresh RPC calls.

    if (setup) {
        await setup();
    }

    function RouterImpl(): JSX.Element {
        const routesLen = routes.length;
        const routeElements = new Array(routesLen + 1);

        for (let i = 0; i < routesLen; i++) {
            const route = routes[i];

            routeElements[i] = (
                <Route
                    key={route.key}
                    path={route.path}
                    element={<route.component />}
                />
            );
        }

        routeElements[routesLen] = (
            <Route
                path="*"
                element={<NotFoundPage key="NotFound" />}
                key={"CatchAll"}
            />
        );

        return (
            <BrowserRouter>
                <Routes>
                    <Route
                        element={<LayoutComponent key="Layout" />}
                        children={routeElements}
                    />
                </Routes>
            </BrowserRouter>
        );
    }

    const Router = React.memo(RouterImpl);

    function App(): JSX.Element {
        return (
            <QueryClientProvider client={queryClient}>
                <WalletProvider>
                    <Router />
                </WalletProvider>
            </QueryClientProvider>
        );
    }

    const rootElement = document.querySelector(rootSelector);
    if (!rootElement) {
        throw new Error("Root element not found");
    }

    const root = createRoot(rootElement);
    root.render(<App />);
}
